/**
 * Tout l'état du scénario dans le hash de l'URL — la fonctionnalité la plus
 * importante du site : dans un débat public, l'objet qui circule est le lien.
 *
 *   #s=<gares>&r=<tracé>&m=<matériel>&k=<perso>&t=<arrêt s>&v=0&g=0&n=<noms>
 *
 * `k` (si m=perso) : masse t, puissance kW, a0, d, vmax km/h, séparés par des virgules.
 *
 * `s` : index de gares triés, delta-encodés en varint LEB128 puis base64url.
 * Une trentaine de gares tiennent en ~50 caractères. Les paramètres à leur
 * valeur par défaut sont omis.
 */
import { DEFAULT_SETTINGS, clampPerso, type PersoParams, type RunSettings } from "../model/run";
import type { StockId } from "../model/rollingstock";

export interface ScenarioState {
  routeId: string;
  stops: number[];
  /** noms personnalisés, par index */
  names: Record<number, string>;
  settings: RunSettings;
}

const STOCK_IDS: StockId[] = ["flirt", "coradia", "civity", "dualis", "citylink"];

// --- varint / base64url ------------------------------------------------------
export function encodeVarints(values: number[]): Uint8Array {
  const out: number[] = [];
  for (let v of values) {
    v = Math.max(0, v | 0);
    while (v >= 0x80) {
      out.push((v & 0x7f) | 0x80);
      v >>>= 7;
    }
    out.push(v);
  }
  return Uint8Array.from(out);
}

export function decodeVarints(bytes: Uint8Array): number[] {
  const out: number[] = [];
  let v = 0;
  let shift = 0;
  for (const b of bytes) {
    v |= (b & 0x7f) << shift;
    if (b & 0x80) {
      shift += 7;
      if (shift > 28) throw new Error("varint trop long");
    } else {
      out.push(v);
      v = 0;
      shift = 0;
    }
  }
  if (shift !== 0) throw new Error("varint tronqué");
  return out;
}

export function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromBase64Url(s: string): Uint8Array {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function encodeStops(stops: number[]): string {
  const sorted = [...new Set(stops)].sort((a, b) => a - b);
  const deltas = sorted.map((v, k) => (k === 0 ? v : v - sorted[k - 1]));
  return toBase64Url(encodeVarints(deltas));
}

export function decodeStops(s: string): number[] {
  const deltas = decodeVarints(fromBase64Url(s));
  const out: number[] = [];
  let acc = 0;
  for (const d of deltas) {
    acc += d;
    out.push(acc);
  }
  return out;
}

// --- matériel personnalisé ----------------------------------------------------
export function encodePerso(p: PersoParams): string {
  const r = (v: number, n: number) => Number(v.toFixed(n)).toString();
  return [r(p.mass_kg / 1000, 1), r(p.power_w / 1000, 0), r(p.a0, 2), r(p.d, 2), r(p.vmax_ms * 3.6, 0)].join(",");
}

export function decodePerso(s: string): PersoParams | null {
  const v = s.split(",").map(Number);
  if (v.length !== 5 || v.some((x) => !Number.isFinite(x))) return null;
  return clampPerso({ mass_kg: v[0] * 1000, power_w: v[1] * 1000, a0: v[2], d: v[3], vmax_ms: v[4] / 3.6 });
}

// --- scénario complet --------------------------------------------------------
export function encodeScenario(st: ScenarioState, defaultRouteId: string): string {
  const p = new URLSearchParams();
  p.set("s", encodeStops(st.stops));
  if (st.routeId !== defaultRouteId) p.set("r", st.routeId);
  const { settings: g } = st;
  if (g.stock !== DEFAULT_SETTINGS.stock) p.set("m", g.stock);
  if (g.stock === "perso" && g.perso) p.set("k", encodePerso(g.perso));
  if (g.tarr !== DEFAULT_SETTINGS.tarr) p.set("t", String(g.tarr));
  if (!g.useVmax) p.set("v", "0");
  if (!g.useGrade) p.set("g", "0");
  const names = Object.entries(st.names).filter(([, n]) => n.trim() !== "");
  if (names.length) {
    p.set("n", names.map(([i, n]) => `${i}:${encodeURIComponent(n)}`).join(";"));
  }
  return p.toString();
}

export function decodeScenario(hash: string, defaultRouteId: string): Partial<ScenarioState> | null {
  const raw = hash.replace(/^#/, "");
  if (!raw) return null;
  const p = new URLSearchParams(raw);
  const out: Partial<ScenarioState> = {};
  try {
    const s = p.get("s");
    if (s !== null) out.stops = decodeStops(s);
  } catch {
    return null;
  }
  out.routeId = p.get("r") ?? defaultRouteId;
  const settings: RunSettings = { ...DEFAULT_SETTINGS };
  const m = p.get("m");
  if (m && (STOCK_IDS as string[]).includes(m)) settings.stock = m as StockId;
  const k = p.get("k");
  if (m === "perso" && k) {
    const perso = decodePerso(k);
    if (perso) {
      settings.stock = "perso";
      settings.perso = perso;
    }
  }
  const t = Number(p.get("t"));
  if (p.get("t") !== null && Number.isFinite(t) && t >= 0 && t <= 600) settings.tarr = t;
  if (p.get("v") === "0") settings.useVmax = false;
  if (p.get("g") === "0") settings.useGrade = false;
  out.settings = settings;
  const n = p.get("n");
  if (n) {
    const names: Record<number, string> = {};
    for (const part of n.split(";")) {
      const k = part.indexOf(":");
      if (k <= 0) continue;
      const idx = Number(part.slice(0, k));
      if (!Number.isInteger(idx)) continue;
      try {
        names[idx] = decodeURIComponent(part.slice(k + 1)).slice(0, 60);
      } catch {
        /* nom illisible : ignoré */
      }
    }
    out.names = names;
  }
  return out;
}
